import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon, ExclamationTriangleIcon, QuestionMarkCircledIcon, GearIcon, ArchiveIcon, EnvelopeClosedIcon } from '@radix-ui/react-icons';
import { useUser } from '../../../contexts';
import { apiCall } from '../../../utils';

const TICKET_TYPES = [
	{ value: 'bug_report', label: 'Bug Report', icon: ExclamationTriangleIcon },
	{ value: 'feature_request', label: 'Feature Request', icon: GearIcon },
	{ value: 'account_issue', label: 'Account Issue', icon: ArchiveIcon },
	{ value: 'scan_problem', label: 'Scan Problem', icon: ExclamationTriangleIcon },
	{ value: 'general', label: 'General Question', icon: QuestionMarkCircledIcon },
];

const PRIORITY_LEVELS = [
	{ value: 'low', label: 'Low', color: 'text-muted-foreground' },
	{ value: 'medium', label: 'Medium', color: 'text-yellow-600' },
	{ value: 'high', label: 'High', color: 'text-orange-600' },
	{ value: 'critical', label: 'Critical', color: 'text-red-600' },
];

export default function BugReportModal({ open, onOpenChange, triggerElement }) {
	const { user } = useUser();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	const [formData, setFormData] = useState({
		type: 'bug_report',
		title: '',
		description: '',
		priority: 'medium',
		technicalDetails: {
			currentUrl: '',
			errorMessage: '',
			componentName: '',
			actionPerformed: '',
		},
	});

	// Capture current context when modal opens
	useEffect(() => {
		if (open) {
			setFormData(prev => ({
				...prev,
				technicalDetails: {
					...prev.technicalDetails,
					currentUrl: window.location.href,
				},
			}));
			setError('');
			setSuccess(false);
		}
	}, [open]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			// Capture browser context
			const context = {
				browser: navigator.userAgent.includes('Chrome') ? 'Chrome' :
					navigator.userAgent.includes('Firefox') ? 'Firefox' :
						navigator.userAgent.includes('Safari') ? 'Safari' : 'Other',
				platform: navigator.platform,
				screenResolution: `${window.screen.width}x${window.screen.height}`,
				currentUrl: window.location.href,
				previousUrl: document.referrer,
			};

			const response = await apiCall('/api/support/tickets', {
				method: 'POST',
				body: JSON.stringify({
					...formData,
					context,
				}),
			});

			if (response.success) {
				setSuccess(true);
				setTimeout(() => {
					onOpenChange(false);
					// Reset form after closing
					setFormData({
						type: 'bug_report',
						title: '',
						description: '',
						priority: 'medium',
						technicalDetails: {
							currentUrl: '',
							errorMessage: '',
							componentName: '',
							actionPerformed: '',
						},
					});
					setSuccess(false);
				}, 2000);
			} else {
				throw new Error(response.error || 'Failed to submit ticket');
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			{triggerElement && <Dialog.Trigger asChild>{triggerElement}</Dialog.Trigger>}
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
				<Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-card rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto z-50">
					<div className="flex items-center justify-between mb-4">
						<Dialog.Title className="text-xl font-semibold flex items-center gap-2">
							<EnvelopeClosedIcon className="w-5 h-5 text-brand-600" />
							Contact Support
						</Dialog.Title>
						<Dialog.Close asChild>
							<button className="p-1 hover:bg-muted rounded">
								<Cross2Icon className="w-4 h-4" />
							</button>
						</Dialog.Close>
					</div>

					{success ? (
						<div className="py-12 text-center">
							<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-green-500/15">
								<svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
								</svg>
							</div>
							<h3 className="text-lg font-semibold mb-2">Ticket Submitted Successfully!</h3>
							<p className="text-muted-foreground">We'll get back to you as soon as possible.</p>
						</div>
					) : (
						<form onSubmit={handleSubmit} className="space-y-4">
							{error && (
								<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
									{error}
								</div>
							)}

							{/* Ticket Type */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Issue Type
								</label>
								<div className="grid grid-cols-2 gap-2">
									{TICKET_TYPES.map(type => {
										const Icon = type.icon;
										return (
											<button
												key={type.value}
												type="button"
												onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
												className={`flex items-center gap-2 p-3 rounded border transition-colors ${formData.type === type.value
													? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200'
													: 'border-input hover:border-input'
													}`}
											>
												<Icon className="w-4 h-4" />
												<span className="text-sm">{type.label}</span>
											</button>
										);
									})}
								</div>
							</div>

							{/* Title */}
							<div>
								<label htmlFor="title" className="block text-sm font-medium text-foreground mb-1">
									Title <span className="text-red-500">*</span>
								</label>
								<input
									id="title"
									type="text"
									required
									value={formData.title}
									onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
									className="w-full px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500"
									placeholder="Brief description of the issue"
								/>
							</div>

							{/* Priority */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Priority
								</label>
								<div className="flex gap-2">
									{PRIORITY_LEVELS.map(level => (
										<button
											key={level.value}
											type="button"
											onClick={() => setFormData(prev => ({ ...prev, priority: level.value }))}
											className={`px-3 py-1 rounded text-sm font-medium transition-colors ${formData.priority === level.value
												? 'bg-foreground text-background'
												: 'bg-muted hover:bg-accent text-foreground'
												}`}
										>
											{level.label}
										</button>
									))}
								</div>
							</div>

							{/* Description */}
							<div>
								<label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
									Description <span className="text-red-500">*</span>
								</label>
								<textarea
									id="description"
									required
									rows={4}
									value={formData.description}
									onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
									className="w-full px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500"
									placeholder="Please provide as much detail as possible about the issue you're experiencing"
								/>
							</div>

							{/* Technical Details (for bug reports) */}
							{formData.type === 'bug_report' && (
								<>
									<div className="border-t pt-4">
										<h3 className="text-sm font-medium text-foreground mb-3">
											Technical Details (Optional)
										</h3>

										<div className="space-y-3">
											<div>
												<label htmlFor="actionPerformed" className="block text-sm text-muted-foreground mb-1">
													What were you trying to do?
												</label>
												<input
													id="actionPerformed"
													type="text"
													value={formData.technicalDetails.actionPerformed}
													onChange={(e) => setFormData(prev => ({
														...prev,
														technicalDetails: {
															...prev.technicalDetails,
															actionPerformed: e.target.value,
														},
													}))}
													className="w-full px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500 text-sm"
													placeholder="e.g., Creating a new scan"
												/>
											</div>

											<div>
												<label htmlFor="errorMessage" className="block text-sm text-muted-foreground mb-1">
													Error message (if any)
												</label>
												<input
													id="errorMessage"
													type="text"
													value={formData.technicalDetails.errorMessage}
													onChange={(e) => setFormData(prev => ({
														...prev,
														technicalDetails: {
															...prev.technicalDetails,
															errorMessage: e.target.value,
														},
													}))}
													className="w-full px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500 text-sm"
													placeholder="Copy any error message you saw"
												/>
											</div>
										</div>
									</div>
								</>
							)}

							{/* User Info Display */}
							<div className="bg-muted p-3 rounded text-sm text-muted-foreground">
								<p>Submitting as: <span className="font-medium">{user?.email}</span></p>
								<p className="text-xs mt-1">We'll send updates to this email address</p>
							</div>

							{/* Submit Button */}
							<div className="flex justify-end gap-3 pt-4">
								<Dialog.Close asChild>
									<button
										type="button"
										className="px-4 py-2 text-foreground hover:bg-muted rounded-md transition-colors"
									>
										Cancel
									</button>
								</Dialog.Close>
								<button
									type="submit"
									disabled={loading || !formData.title || !formData.description}
									className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
								>
									{loading ? 'Submitting...' : 'Submit Ticket'}
								</button>
							</div>
						</form>
					)}
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
